import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  useMediaQuery,
  Grid,
  Checkbox,
  MenuItem,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

// COMPONENTS
import Slide from '@/components/Transitions/Slide';
import Link from '@/components/Link';

// HELP
import MultiselectSearch from '../MultiselectSearch';
import SelectedSection from '../SelectedSection';

// STYLES
import { StyledContolLabel, SelectStyled, DialogStyled } from './styles';

const Filters = props => {
  const {
    openFilters,
    toggleFilters,
    values,
    options,
    handleChange,
    handleReset,
    loadingFilters,
    link,
    isFreeApps,
  } = props;
  const [t] = useTranslation();
  const { isIframe, isAuth } = useSelector(({ app, auth }) => ({
    isIframe: app.isIframe,
    isAuth: auth.isAuth,
  }));
  const upMd = useMediaQuery(({ breakpoints }) => breakpoints.up('md'));
  const upSm = useMediaQuery(({ breakpoints }) => breakpoints.up('sm'));

  const isSelectedApps = values.appsType === options.appsTypes[isAuth ? 1 : 0].id;
  const isMyApps = isAuth ? values.appsType === options.appsTypes[0].id : false;

  const renderTypeCheckbox = type => {
    const checked = values.type.includes(type.value);

    let newValue;

    if (checked) {
      newValue = values.type.filter(el => el !== type.value);
    } else {
      newValue = [...values.type, type.value];
    }

    return (
      <Checkbox
        className="control-label__checkbox"
        name="type"
        value={type.value}
        checked={checked}
        onChange={() => handleChange('type', newValue)}
      />
    );
  };

  const handleDelete = name => item => () => {
    const selectedData = values[name].slice();

    const selectedIndex = selectedData.indexOf(item.id);
    selectedData.splice(selectedIndex, 1);

    handleChange(name, selectedData);
  };

  const getSelectedOptions = (a, b) => a.filter(el => b.includes(el.id));

  const selectedApps = getSelectedOptions(options.apps, values.apps);
  const selectedGenres = getSelectedOptions(options.genres, values.genres);

  const selectedContent =
    selectedApps.length || selectedGenres.length ? (
      <div className="content-filters__selected-filters">
        <Grid container spacing={upSm ? 4 : 3}>
          {!!selectedApps.length && (
            <Grid item xs={12}>
              <SelectedSection
                data={selectedApps}
                title={t(`showsAndMovies.selectedFilters.apps`)}
                onDelete={handleDelete('apps')}
                clickable={!(isMyApps || isFreeApps)}
              />
            </Grid>
          )}
          {!!selectedGenres.length && (
            <Grid item xs={12}>
              <SelectedSection
                data={selectedGenres}
                title={t(`showsAndMovies.selectedFilters.genres`)}
                onDelete={handleDelete('genres')}
              />
            </Grid>
          )}
        </Grid>
      </div>
    ) : null;

  const filterButtons = (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={4}>
        <Button color="primary" fullWidth component={Link} to={link}>
          {t('buttons.search')}
        </Button>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Button
          className="filters-dialog__reset-filters"
          variant="outlined"
          color="secondary"
          fullWidth
          onClick={handleReset}
        >
          {t('buttons.resetFilters')}
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <DialogStyled
      open={openFilters}
      onClose={toggleFilters}
      className="shows-and-movies-filters-dialog filters-dialog"
      fullScreen
      disableRestoreFocus
      disablePortal
      TransitionComponent={Slide}
      closeAfterTransition
      PaperProps={{
        elevation: 0,
      }}
    >
      <DialogTitle className="filters-dialog__header" disableTypography>
        <IconButton
          className="filters-dialog__close-button"
          color="secondary"
          aria-label="close filters"
          onClick={toggleFilters}
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        className={clsx('filters-dialog__content', isIframe && 'filters-dialog__content--iframe')}
      >
        <Typography variant="h4" align={upSm ? 'left' : 'center'}>
          {t('showsAndMovies.filters.searchTitle')}
        </Typography>
        <Typography className="filters-dialog__content-title" variant="h6">
          {t('showsAndMovies.title')}
        </Typography>
        <div className="filters-dialog__content-filters content-filters">
          <Grid
            container
            spacing={upSm ? 4 : 1}
            alignItems="center"
            wrap={upMd ? 'nowrap' : 'wrap'}
          >
            <Grid item xs={12} md={3}>
              <MultiselectSearch
                name="genres"
                placeholder={t('showsAndMovies.filters.genresPlaceholder')}
                data={options.genres}
                selected={values.genres}
                onChange={handleChange}
                size="small"
                disabled={loadingFilters}
              />
            </Grid>
            <Grid item xs={12} md={isSelectedApps ? 3 : 6}>
              <SelectStyled
                className="select"
                name="appsType"
                value={values.appsType}
                onChange={({ target: { name, value } }) => handleChange(name, value)}
                variant="filled"
                displayEmpty
                disabled={loadingFilters}
                disableUnderline
                MenuProps={{
                  disablePortal: true,
                  transitionDuration: 0,
                }}
              >
                {options.appsTypes.map(appsType => {
                  return (
                    <MenuItem
                      key={`device-${appsType.id}`}
                      className="select__option"
                      value={appsType.id}
                    >
                      {appsType.name}
                    </MenuItem>
                  );
                })}
              </SelectStyled>
            </Grid>
            {isSelectedApps && (
              <Grid item xs={12} md={3}>
                <MultiselectSearch
                  name="apps"
                  placeholder={t('showsAndMovies.filters.appsPlaceholder')}
                  data={options.apps}
                  selected={values.apps}
                  onChange={handleChange}
                  size="small"
                  disabled={loadingFilters}
                />
              </Grid>
            )}
            <Grid item xs={12} md="auto">
              <Grid container spacing={3} wrap="nowrap">
                {options.types.map((type, index) => (
                  <Grid item key={index} xs={6} sm="auto">
                    <StyledContolLabel
                      className="control-label"
                      control={renderTypeCheckbox(type)}
                      label={t(type.text)}
                      disabled={loadingFilters}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </div>
        {selectedContent}
        {upSm && <div className="content-filters__buttons">{filterButtons}</div>}
      </DialogContent>
      {!upSm && <DialogActions className="filters-dialog__footer">{filterButtons}</DialogActions>}
    </DialogStyled>
  );
};

Filters.propTypes = {
  openFilters: PropTypes.bool.isRequired,
  toggleFilters: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  options: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleReset: PropTypes.func.isRequired,
  loadingFilters: PropTypes.bool.isRequired,
  link: PropTypes.string.isRequired,
  isFreeApps: PropTypes.bool.isRequired,
};

export default React.memo(Filters);
